import {
  AppProvider,
  Button,
  Stack,
  Page,
  Heading,
} from '@envoy/polarwind';
import React, { Component } from 'react';
import './App.css';
import Rule from './Rule';

class App extends Component {

  state = {
    loading: true,
    rules: null,
    editingIndex: null,
    editingRule: null,
    tableOptions: null,
    visitorTypeOptions: null,
    error: null,
  };

  async componentDidMount() {
    const { api } = this.props;
    const [
      tableOptions,
      rules,
      visitorTypeOptions,
    ] = await Promise.all([api.getTableOptions(), api.getRules(), api.getVisitorTypeOptions()]);
    
    this.setState({ tableOptions, rules, visitorTypeOptions, loading: false });
  }

  /* 
    Set the current editing index to point to the new rule being edited
    And create a new rule with Rule's default rule.
  */
  onAddNewRule = () => {
    this.setState({
      editingIndex: this.state.rules.length,
      editingRule: Rule.defaultRule,
    });
  };

  onCancelEditingRule = () => {
    this.setState({ editingIndex: null, editingRule: null });
  };

  onDoneEditingRule = () => {
    const rules = [...this.state.rules];
    rules[this.state.editingIndex] = this.state.editingRule;
    this.setState({ rules, editingIndex: null, editingRule: null });
  };

  onEditRule = editingIndex => {
    const editingRule = Object.assign({}, this.state.rules[editingIndex]);
    this.setState({ editingIndex, editingRule });
  };

  onChangeRule = editingRule => {
    const conflictingRule = this.state.rules.find((rule, index) => {
      if (index === this.state.editingIndex) {
        return false;
      }
      return rule.action === editingRule.action && rule.table === editingRule.table;
    });
    if (conflictingRule) {
      throw new Error('A rule already exists for this table and action.');
    }
    this.setState({ editingRule });
  };

  onRemoveRule = () => {
    const { rules, editingIndex } = this.state;
    this.setState({
      rules: rules.filter((r, i) => i !== editingIndex),
      editingIndex: null,
      editingRule: null,
    });
  };

  onSaveAndComplete = e => {
    this.setState({ loading: true }, async () => {
      try {
        await this.props.api.saveRules(this.state.rules);
        // Get correct react app close url for the appropriate environment
        const REACT_APP_CLOSE_URL = await this.props.api.getReactCloseURL();
        
        const closeURL = document.createElement('a');
        closeURL.href = REACT_APP_CLOSE_URL
        closeURL.search = `?state=${this.props.api.params.close}`;
        window.location = closeURL;
      } catch (err) {
        this.setState({ loading: false, error: err.message });
      }
    });
  };

  get rules() {
    const { rules, tableOptions, visitorTypeOptions, editingIndex, editingRule } = this.state;
    if (!rules) {
      return (
        <p>
          Loading...
        </p>
      );
    }

    if (!rules.length && !editingRule) {
      return (
        <p>
          No rules found. Click the "Add new rule" button below to get started.
        </p>
      );
    }

    return rules.map((rule, index) => (
      <Rule
        key={index}
        index={index}
        api={this.props.api}
        isOpen={editingIndex === index}
        rule={editingIndex === index ? editingRule : rule}
        tableOptions={tableOptions}
        visitorTypeOptions={visitorTypeOptions}
        rules={rules.filter((r, i) => i !== index)}
        onCancel={this.onCancelEditingRule}
        onDone={this.onDoneEditingRule}
        onEdit={this.onEditRule}
        onChange={this.onChangeRule}
        onRemove={this.onRemoveRule}
      />
    ));
  }

  get newRule() {
    const { rules, tableOptions, visitorTypeOptions, editingIndex, editingRule } = this.state;
    if (!rules || !editingRule || editingIndex !== rules.length) {
      return null;
    }
    return (
      <Rule
        index={editingIndex}
        api={this.props.api}
        isOpen={true}
        rule={editingRule}
        tableOptions={tableOptions}
        visitorTypeOptions={visitorTypeOptions}
        rules={rules}
        onCancel={this.onCancelEditingRule}
        onDone={this.onDoneEditingRule}
        onEdit={this.onEditRule}
        onChange={this.onChangeRule}
      />
    )
  }

  render() {
    const { loading, editingRule } = this.state;
    return (
      <div className="Polarwind-Page Polarwind-Page--withHeader">
        <Heading element="h1" size="extraLarge">ServiceNow Rule Manager</Heading><br />
        {this.rules}
        {this.newRule}
        <hr />
        <Stack distribution="equalSpacing">
          <Button disabled={loading} onClick={this.onAddNewRule} brandOutline>
            Add new rule
          </Button>
          <Button disabled={loading || editingRule} onClick={this.onSaveAndComplete}>
            Save and complete
          </Button>
        </Stack>
      </div>
    );
  }
}

export default App;